// frontend/src/components/chat/ChatDocumentUpload.js
// Conversation-scoped document upload for immediate Q&A

import React, { useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, LinearProgress, Chip, 
  IconButton, Alert, List, ListItem, ListItemText, ListItemSecondaryAction,
  Collapse, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  ExpandLess, ExpandMore,
  Check as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as ProcessingIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const ChatDocumentUpload = ({ 
  conversationId,
  ensureConversationId,  // async () => id: creates the conversation when the chat has none yet (DT-1294)
  brandId,
  expertId,  // NEW: Required expert ID
  onDocumentProcessed,
  onDocumentDeleted,
  onConversationDenied,  // (conversationId) => void: the server refused this conversation; the parent drops it (DT-1514)
  compact = false
}) => {
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [processing, setProcessing] = useState({});
  const [errors, setErrors] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // A new drop is a new attempt: the last attempt's failures would otherwise stay in the dialog next to it (DT-1929)
    setErrors([]);

    // Validate expertId is provided
    if (!expertId) {
      setErrors(prev => [...prev, {
        filename: 'Upload Error',
        error: 'Expert must be selected before uploading documents'
      }]);
      return;
    }
    
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const newErrors = rejectedFiles.map(file => ({
        filename: file.file.name,
        error: file.errors.map(e => e.message).join(', ')
      }));
      setErrors(prev => [...prev, ...newErrors]);
    }
    
    if (acceptedFiles.length === 0) return;

    // The upload is linked to a real conversation: in a fresh chat it is created first, once for the whole drop.
    // A missing id must never be sent: FormData would send the text "null", which the server refuses (DT-1294).
    let uploadConversationId = conversationId;
    try {
      if (!uploadConversationId && ensureConversationId) uploadConversationId = await ensureConversationId();
    } catch (error) {
      uploadConversationId = null;
    }
    if (!uploadConversationId) {
      const message = 'Could not start a conversation for this upload. Please try again.';
      setErrors(prev => [...prev, ...acceptedFiles.map(file => ({ filename: file.name, error: message }))]);
      // the compact dialog lists errors too (DT-1929), but a fresh-chat drop can come before it is on screen: also say it
      // the way a failed send does
      if (compact) alert(message);
      return;
    }

    // Process accepted files
    for (const file of acceptedFiles) {
      // the rest would be refused the same way
      if (await uploadFile(file, uploadConversationId) === 'denied') break;
    }
  }, [conversationId, ensureConversationId, brandId, expertId, compact, onConversationDenied]);

  const uploadFile = async (file, uploadConversationId) => {
    const fileId = `${file.name}_${Date.now()}`;

    try {
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      const formData = new FormData();
      formData.append('document', file);
      formData.append('brandId', brandId);
      formData.append('expertId', expertId);  // NEW: Include expert ID
      formData.append('conversationId', uploadConversationId);
      formData.append('uploadType', 'chat_context');
      
      const response = await fetch('/api/chat/documents/upload-chat', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          const denied = await response.clone().json().catch(() => ({}));
          if (denied.code === 'CONVERSATION_ACCESS_DENIED') {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });
            // the parent says it in the chat; without a parent, say it here
            if (onConversationDenied) onConversationDenied(uploadConversationId);
            else setErrors(prev => [...prev, { filename: file.name, error: 'That conversation is not available to you, so a new one will start. Upload the file again.' }]);
            // close the compact dialog: it would hide the parent's warning in the chat
            setUploadDialogOpen(false);
            return 'denied';
          }
        }
        throw new Error(`Upload failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        setProcessing(prev => ({ ...prev, [fileId]: true }));
        
        const newDoc = {
          ...result.document,
          fileId,
          filename: file.name,
          uploadedAt: new Date(),
          status: 'processing',
          uploadConversationId // the conversation it belongs to: shown only while that conversation is on screen
        };
        
        setUploadedDocs(prev => [...prev, newDoc]);
        
        // Start monitoring processing status
        monitorProcessingStatus(result.document.id, fileId, uploadConversationId);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setErrors(prev => [...prev, {
        filename: file.name,
        error: error.message
      }]);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
    }
  };
  
  const monitorProcessingStatus = async (documentId, fileId, uploadConversationId) => {
    const maxPolls = 20; // 3-4 minutes max
    let pollCount = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(`/api/chat/documents/status/${documentId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          setUploadedDocs(prev => prev.map(doc => 
            doc.fileId === fileId 
              ? { ...doc, status: data.status, chunk_count: data.chunk_count }
              : doc
          ));
          
          if (data.status === 'ready') {
            setProcessing(prev => {
              const newProcessing = { ...prev };
              delete newProcessing[fileId];
              return newProcessing;
            });
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileId];
              return newProgress;
            });
            
            onDocumentProcessed?.(documentId, data, uploadConversationId);
            return;
          }
          
          if (data.status === 'failed') {
            setErrors(prev => [...prev, {
              filename: fileId,
              error: 'Document processing failed'
            }]);
            setProcessing(prev => {
              const newProcessing = { ...prev };
              delete newProcessing[fileId];
              return newProcessing;
            });
            return;
          }
          
          if (pollCount < maxPolls && data.status === 'processing') {
            pollCount++;
            setTimeout(poll, 10000); // Poll every 10 seconds
          }
        }
      } catch (error) {
        console.error('Status polling failed:', error);
        setProcessing(prev => {
          const newProcessing = { ...prev };
          delete newProcessing[fileId];
          return newProcessing;
        });
      }
    };
    
    setTimeout(poll, 2000); // Start polling after 2 seconds
  };
  
  const removeDocument = async (fileId, documentId) => {
    try {
      if (documentId) {
        // Updated endpoint to include expertId
        const response = await fetch(`/api/chat/documents/${brandId}/${expertId}/${documentId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          onDocumentDeleted?.(documentId);
        }
      }
      
      setUploadedDocs(prev => prev.filter(doc => doc.fileId !== fileId));
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
      setProcessing(prev => {
        const newProcessing = { ...prev };
        delete newProcessing[fileId];
        return newProcessing;
      });
    } catch (error) {
      console.error('Failed to remove document:', error);
    }
  };
  
  const getStatusIcon = (status, fileId) => {
    if (processing[fileId]) return <ProcessingIcon color="action" />;
    switch (status) {
      case 'ready': return <CheckIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      default: return <FileIcon />;
    }
  };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/json': ['.json']
    },
    maxSize: 20 * 1024 * 1024, // 20MB for chat uploads
    maxFiles: 3,
    multiple: true
  });
  
  // Only this conversation's uploads: after a reset (new conversation, expert/brand switch, another conversation
  // opened) an upload made for the earlier chat, including one still finishing, is not shown in the new one (DT-1562)
  const shownDocs = uploadedDocs.filter(doc => doc.uploadConversationId === conversationId);

  // Each failed upload / processing step, dismissible. Both views render it: the compact dialog (what the chat screen
  // uses) showed none, so a failed upload just vanished from it (DT-1929).
  const errorAlerts = errors.length > 0 && (
    <Box sx={{ mt: 2 }}>
      {errors.map((error, index) => (
        <Alert key={index} severity="error" sx={{ mb: 1 }} onClose={() => {
          setErrors(prev => prev.filter((_, i) => i !== index));
        }}>
          <strong>{error.filename}:</strong> {error.error}
        </Alert>
      ))}
    </Box>
  );

  if (compact) {
    return (
      <Box>
        {/* Compact upload button */}
        <IconButton 
          onClick={() => setUploadDialogOpen(true)}
          size="small"
          sx={{ 
            color: shownDocs.length > 0 ? 'primary.main' : 'text.secondary',
          }}
          title={expertId ? "Upload documents for this conversation" : "Select an expert first"}
          disabled={!expertId}
        >
          <AttachFileIcon />
          {shownDocs.length > 0 && (
            <Box 
              component="span" 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                right: 0, 
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 16,
                height: 16,
                fontSize: '0.7rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {shownDocs.length}
            </Box>
          )}
        </IconButton>
        
        {/* Document status chips */}
        {shownDocs.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {shownDocs.map((doc) => (
              <Chip
                key={doc.fileId}
                label={doc.filename}
                size="small"
                icon={getStatusIcon(doc.status, doc.fileId)}
                onDelete={() => removeDocument(doc.fileId, doc.id)}
                color={doc.status === 'ready' ? 'success' : doc.status === 'failed' ? 'error' : 'default'}
                variant="outlined"
              />
            ))}
          </Box>
        )}
        
        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Documents for Chat</DialogTitle>
          <DialogContent>
            <Paper
              {...getRootProps()}
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                bgcolor: isDragActive ? 'primary.50' : 'background.paper',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6">
                {isDragActive ? "Drop files here" : "Upload Chat Documents"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
              Drag & drop files or click to browse (PDF, DOCX, TXT, CSV, etc.)
                <br />
              <strong>Expert:</strong> {expertId ? 'Selected' : 'None Selected - Please select an expert first'}
            </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Max 20MB per file, up to 3 files
              </Typography>
            </Paper>
            {errorAlerts}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
  
  // Full upload interface
  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2">
          Chat Documents ({shownDocs.length})
        </Typography>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Paper
            {...getRootProps()}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              border: '1px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              bgcolor: isDragActive ? 'primary.50' : 'background.paper',
              mb: 2
            }}
          >
            <input {...getInputProps()} />
            <AttachFileIcon sx={{ mr: 1 }} />
            <Typography variant="body2">
              {isDragActive ? "Drop files here" : "Drop files or click to upload"}
            </Typography>
          </Paper>
          
          {/* Upload Progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <Box sx={{ mb: 2 }}>
              {Object.entries(uploadProgress).map(([fileId, progress]) => (
                <Box key={fileId} sx={{ mb: 1 }}>
                  <Typography variant="caption">{fileId}: {progress}%</Typography>
                  <LinearProgress variant="determinate" value={progress} />
                </Box>
              ))}
            </Box>
          )}
          
          {/* Uploaded Documents */}
          {shownDocs.length > 0 && (
            <List dense>
              {shownDocs.map((doc) => (
                <ListItem key={doc.fileId}>
                  {getStatusIcon(doc.status, doc.fileId)}
                  <ListItemText
                    primary={doc.filename}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Status: {doc.status || 'uploading'}
                        </Typography>
                        {doc.chunk_count > 0 && (
                          <Typography variant="caption" color="success.main">
                            {doc.chunk_count} chunks ready
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      onClick={() => removeDocument(doc.fileId, doc.id)} 
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
          
          {/* Errors */}
          {errorAlerts}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ChatDocumentUpload;
